import React from "react";
import styled from "styled-components";

// List Layout Styling
const StyledListCard = styled.div`
  background-color: var(--bg-card);
  border-top: 1px solid var(--border-color);
  padding-top: 2rem;
  padding-bottom: 2rem;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  transition: border-color 0.25s ease;
  box-sizing: border-box;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 3.2fr;
    gap: 2rem;
  }
`;

// Card Box Layout Styling
const StyledBoxCard = styled.div`
  background-color: #ffffff;
  border: 1px solid var(--border-color);
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  transition: border-color 0.25s ease;
  border-radius: 4px;
  margin-bottom: 1.5rem;

  &:hover {
    border-color: var(--accent);
    cursor: pointer;
    h3 {
      color: var(--accent);
    }
  }

  > h3 {
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 1.3rem;
    font-weight: 700;
    margin-top: 0.5rem;
    margin-bottom: 0.75rem;
    line-height: 1.3;
    transition: color 0.25s ease;
    color: var(--text-primary);
  }

`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-muted);
  
  .company {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05rem;
    color: var(--text-muted);
    font-weight: 600;
    margin-top: 0.25rem;
  }

  .metadata-image {
    margin-top: auto;
    margin-bottom: auto;
  }

  @media (max-width: 767px) {
    .metadata-image {
      width: 64px;
      margin-top: 0.75rem;
      margin-bottom: 0;
    }
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const BoxMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04rem;
  margin-bottom: 0.25rem;
  
  .company {
    color: var(--text-muted);
  }
  
  .dot {
    opacity: 0.5;
  }
`;

const Title = styled.h3`
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: -0.02rem;
  margin-top: 0;
  margin-bottom: 0.5rem;
  line-height: 1.3;
  color: var(--text-primary);
  transition: color 0.2s ease;

  &:hover {
    color: var(--accent);
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  margin-bottom: 0.75rem;
  
  .hashtag {
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--tag-color);
    // transition: color 0.2s ease;
    
    // &:hover {
    //   color: var(--tag-hover);
    // }
  }
`;

const Description = styled.div`
  font-size: 1.125rem;
  line-height: 1.6;
  color: var(--text-primary);
  flex-grow: 1;

  > p {
    margin: 0 0 1rem;
    color: inherit;

    &:last-child {
      margin-bottom: 1rem;
    }
  }
`;

const ReadMore = styled.span`
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05rem;
  color: var(--accent);
  border-bottom: 1px solid transparent;
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    color: var(--accent-hover);
    border-bottom-color: currentColor;
  }
`;

const CardImageContainer = styled.div`
  margin-top: 0.5rem;
  margin-bottom: 1rem;
  max-width: 100%;
  overflow: hidden;
  border: none;
  line-height: 0;
  border-radius: 4px;

  > img {
    width: 100%;
    height: auto;
    display: block;
    border: none;
  }
`;

export default function Card(props) {
  // Format standard dates to "May 13, 2026" or similar readable format
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    if (/^\d{4}$/.test(dateStr)) return dateStr;

    try {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      const date = new Date(dateStr);
      // Validate that it's a valid date
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-US', options);
    } catch (e) {
      return dateStr;
    }
  };

  const parsedDate = formatDate(props.date);
  const viewMode = props.viewMode || "list";
  const descriptionParagraphs = Array.isArray(props.children)
    ? props.children
    : [props.children];
  const description = (
    <Description>
      {descriptionParagraphs.map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}
    </Description>
  );

  if (viewMode === "card") {
    return (
      <StyledBoxCard className="stagger-item" style={props.style}>
        <BoxMeta>
          {parsedDate && <time dateTime={props.date}>{new Date(props.date).getFullYear() || props.date}</time>}
          {props.tag && (
            <>
              <span className="dot">•</span>
              <div className="company">{props.tag}</div>
            </>
          )}
        </BoxMeta>
        <h3>{props.title ? props.title : "Untitled Project"}</h3>
        {props.tags && props.tags.length > 0 && (
          <TagList>
            {props.tags.map((t) => (
              <span key={t} className="hashtag">
                {t}
              </span>
            ))}
          </TagList>
        )}
        {props.img && (
          <CardImageContainer>
            <img alt="cover art" src={props.img} />
          </CardImageContainer>
        )}
        {description}
        <ReadMore style={{ marginTop: "auto" }}>Read more →</ReadMore>
      </StyledBoxCard>
    );
  }

  // Default List mode
  return (
    <StyledListCard className="stagger-item" style={props.style}>
      <LeftColumn>
        {parsedDate && <time dateTime={props.date}>{parsedDate}</time>}
        {props.tag && <div className="company">{props.tag}</div>}
        {props.img && (
          <CardImageContainer className="metadata-image">
            <img alt="cover art" src={props.img} />
          </CardImageContainer>
        )}
      </LeftColumn>
      <RightColumn>
        <Title>{props.title ? props.title : "Untitled Project"}</Title>
        {props.tags && props.tags.length > 0 && (
          <TagList>
            {props.tags.map((t) => (
              <span key={t} className="hashtag">
                {t}
              </span>
            ))}
          </TagList>
        )}
        {description}
        <ReadMore>Read more →</ReadMore>
      </RightColumn>
    </StyledListCard>
  );
}
